import axios from 'axios'
import appSettings from '@/app-settings.json'

/**
 *  Set of function to find an address using postcodes
 */
export default class PostcodeLookup {
  /**
   * Constructor
   * @param {string} key - The pca predict api key to use
   */
  constructor (key) {
    // Create axios instance
    this.axios_instance = axios.create()

    // Add key to every request
    this.axios_instance.interceptors.request.use((config) => {
      config.params.Key = key
      return config
    })

    // Refactor response to make them more useable.
    this.axios_instance.interceptors.response.use((response) => {
      if (response.data && response.data.Items && response.data.Items.length) {
        return response.data.Items
      }
      return []
    })

    // The AddressResult factory to produce AddressResults
    this.addressResultFactory = new AddressResultFactory()
  }

  /**
   * Looks up an address given its container id, this can be found by first running
   * a postcode search
   *
   * @param {string} id - The container id
   * @returns {Promise<AddressResult[]>} A promise containing arrary of AddressResults
   */
  addressLookup (id) {
    // Using pca predict retrive api
    // {#link: https://www.pcapredict.com/address-validation/api/}
    return this.axios_instance.get(appSettings.pcaPredictConfig.retrieveUrl, {
      params: {
        Id: id
      }
    }).then((results) => {
      return results.map(result => {
        return new AddressResult(
          this.addressResultFactory.addressResultfromPCAFindResult(result)
        )
      })
    })
  }

  /**
   * Looks up a postcode returning the addresses related to that postcode
   *
   * @param {string} text - The postcode
   * @param  {string} countries - The countries to search in (default: GB)
   * @returns {Promise<PostcodeResult[]>} A promise containing arrary of postcodeResults
   */
  postcodeSearch (postcode) {
    // Make a query to the find api searching for postcode
    // {#link: https://www.pcapredict.com/address-validation/api/}
    return this.axios_instance.get(appSettings.pcaPredictConfig.findUrl, {
      params: {
        Text: postcode
      }
    }).then((response) => {
      // Look though query results for postcode, return the first postcode
      for (let entry of response) {
        if (entry.Type === 'Postcode') {
          return entry.Id
        }
      }

      // If no postcods are found
      throw new Error('No matching postcode found')
    }).then((containerID) => {
      // Query for the addresses related to the postcode
      return this.axios_instance.get(appSettings.pcaPredictConfig.findUrl, {
        params: {
          Container: containerID
        }
      })
    }).then((results) => {
      return results.map(result => {
        return new PostcodeResult(result.Id, result.Text, result.Description)
      })
    })
  }

  /**
   * Looks up a address/postcode returning the addresses related to the specified text
   *
   * @param {string} text - The text (address or postcode)
   * @returns {Promise<PostcodeResult[]>} A promise containing arrary of postcodeResults
   */
  addressSearch (text) {
    // Make a query to the find api searching for postcode
    // {#link: https://www.pcapredict.com/address-validation/api/}
    return this.axios_instance.get(appSettings.pcaPredictConfig.findUrl, {
      params: {
        Text: text
      }
    }).then((response) => {
      let addresses = []
      // If the entry type is NOT ‘Address’ then the Id should be passed as the Container to a further Find request to get more results
      // Else, if the entry type is 'Address' then the Id can be saved and later passed to the Retrieve service for the full address
      for (let entry of response) {
        if (entry.Type !== 'Address') {
          return this.postcodeLookup(entry.Id)
        } else {
          addresses.push(new PostcodeResult(entry.Id, entry.Text, entry.Description))
        }
      }

      // If we get here then no postcode was found, but addresses may have been. Let's check
      if (addresses.length > 0) {
        return addresses
      } else {
        // If no postcodes or addresses are found
        throw new Error('No matching address found')
      }
    })
  }

  /**
   * Find Addresses from a Postcode result
   * We need to do this as Postcode Ids can't be passed directly to the retrieve address API
   * they first need to be passed back to the find API for a list of addresses
   *
   * @param {string} containerId - The container id for the postcode returned from the find
   * addresses api. See addressSearch
   * @returns {Promise<PostcodeResult[]>} A promise containing arrary of postcodeResults
   */
  postcodeLookup (containerId) {
    // Query for the addresses related to the postcode
    return this.axios_instance.get(appSettings.pcaPredictConfig.findUrl, {
      params: {
        Container: containerId
      }
    }).then((results) => {
      return results.map(result => {
        return new PostcodeResult(result.Id, result.Text, result.Description)
      })
    })
  }
}

/**
 * Class to hold Postcode Search results
 * TODO change name to AddressFindResult as we now support address and postcode
 * lookup
 */
export class PostcodeResult {
  /**
   * @param {string} id - The id of the result
   * @param {string} text - The text of the result
   * @param {string} description - The description of the result
   */
  constructor (id, text, description) {
    this._id = id
    this._text = text
    this._description = description
  }

  get id () { return this._id }
  set id (newId) { this._id = newId }

  get text () { return this._text }
  set text (newText) { this._text = newText }

  get description () { return this._description }
  set description (newDescription) { this._description = newDescription }
}

/**
 * AddressResult Factory class, providing factories to produce
 * AddressResult's
 */
// todo don't export this, should be private i think??
export class AddressResultFactory {
  /**
   * Creates a AddressResult from the result of a PCA Find request
   * {#link: https://www.pcapredict.com/address-validation/api/}
   *
   * @param  {Object} result - The result from a PCA find request
   * @returns {AddressResult} The create AddressResult
   */
  addressResultfromPCAFindResult (result) {
    console.log(result)
    return new AddressResult({
      address1: result.Line1,
      address2: result.Line2,
      address3: result.Line3,
      city: result.City,
      county: result.ProvinceName,
      postcode: result.PostalCode,
      country: result.CountryName,
      countryIso2: result.CountryIso2,
      countryIso3: result.CountryIso3,
      id: result.Id
    })
  }
}

/**
 * Class to hold Address lookup results
 */
export class AddressResult {
  /**
   * @param {Object} data
   * @param {string} data.address1 - The address1
   * @param {string} data.address2 - The address2
   * @param {string} data.address3 - The address3
   * @param {string} data.city - The city
   * @param {string} data.county - The county
   * @param {string} data.postcode - The postcode
   * @param {string} data.country - The country
   * @param {string} data.countryIso2 - The country iso2 code
   * @param {string} data.countryIso3 - The country iso3 code
   * @param {string} data.id - The id
   */
  constructor (data) {
    this._address1 = data.address1
    this._address2 = data.address2
    this._address3 = data.address3
    this._city = data.city
    this._county = data.county
    this._postcode = data.postcode
    this._country = data.country
    this._countryIso2 = data.countryIso2
    this._countryIso3 = data.countryIso3
    this._id = data.id
  }

  get address1 () { return this._address1 }
  set address1 (newAddress1) { this._address1 = newAddress1 }

  get address2 () { return this._address2 }
  set address2 (newAddress2) { this._address2 = newAddress2 }

  get address3 () { return this._address3 }
  set address3 (newAddress3) { this._address3 = newAddress3 }

  get city () { return this._city }
  set city (newCity) { this._city = newCity }

  get county () { return this._county }
  set county (newCounty) { this._county = newCounty }

  get postcode () { return this._postcode }
  set postcode (newPostcode) { this._postcode = newPostcode }

  get country () { return this._country }
  set country (newCountry) { this._country = newCountry }

  get countryIso2 () { return this._countryIso2 }
  set countryIso2 (newCountryIso2) { this._countryIso2 = newCountryIso2 }

  get countryIso3 () { return this._countryIso3 }
  set countryIso3 (newCountryIso3) { this._countryIso3 = newCountryIso3 }

  get id () { return this._id }
  set id (newId) { this._id = newId }
}
